/* eslint-disable max-len */

import React from "react";
import { Routes as RRRoutes, Route } from "react-router";
import PropTypes from "prop-types";
import {
	HomePage,
	ErrorPage,
	StyleGuide,
	FacilityPage,
	ConfirmationPage,
	OMIPage,
	SelfStoragePage,
	CheapStoragePage,
	MilitaryStoragePage,
	SearchPage,
	TermsAndConditionsPage,
	PaidLandingPage,
	SizeGuidePage,
	PrivacyPolicyPage,
	HelpPage,
	ContactUsPage,
	StateLandingPage,
	StyleGuidePage,
	PSPPage,
	UnitPageV2
} from "pages/Bundles";

import {
	getSearchPage,
	getLandingPage,
	getNearMePage
} from "./store/modules/search";
import { LANDING_PAGE_QUERY } from "./utils/search/buildLandingQuery";
import { getStateLandingPage } from "./store/modules/state";
import { getFacilityPage } from "./store/modules/facilities";
import { getBookingDetails } from "./store/modules/booking";
import { getUnit } from "./store/modules/units";

export const staticRoutes = [
	{
		path: "/",
		component: HomePage
	},
	{
		// React Router v6 dropped regex path params (was :keyword(a|b|c)), which
		// is why valid NMP slugs 404'd. The slug is validated in getNearMePage;
		// unknown slugs render the empty search state (no gateway fetch).
		path: "/storage-units-near-me/:keyword/",
		component: SearchPage,
		actions: { getNearMePage },
		loadData: ({ actions, match, queryParams }) =>
			actions.getNearMePage(match.params, queryParams)
	},
	{
		path: "/search/",
		component: SearchPage,
		actions: { getSearchPage },
		loadData: ({ actions, queryParams }) =>
			actions.getSearchPage({ query: queryParams })
	},
	{
		path: "/reservation/confirmation/:bookingId/",
		component: ConfirmationPage,
		actions: { getBookingDetails },
		loadData: ({ actions, match, queryParams }) => {
			const {
				params: { bookingId }
			} = match;
			return actions.getBookingDetails({
				query: { ...queryParams, bookingEmail: queryParams.email },
				path: `${bookingId}`
			});
		}
	},
	{
		path: "/reservation/online-move-in/:bookingId/",
		component: OMIPage,
		actions: { getBookingDetails },
		loadData: ({ actions, match, queryParams }) => {
			const {
				params: { bookingId }
			} = match;
			return actions.getBookingDetails({
				query: { ...queryParams, bookingEmail: queryParams.email },
				path: `${bookingId}`
			});
		}
	},
	{
		path: "/self-storage/",
		component: SelfStoragePage
	},
	{
		path: "/cheap-storage/",
		component: CheapStoragePage
	},
	{
		path: "/military-storage/",
		component: MilitaryStoragePage
	},
	{
		path: "/storage-solutions/:keyword/",
		component: PSPPage
	},
	{
		path: "/self-storage/:state/",
		component: StateLandingPage,
		actions: { getStateLandingPage },
		loadData: ({ actions, match, queryParams }) => {
			const {
				params: { state }
			} = match;
			return actions.getStateLandingPage({
				query: queryParams,
				path: `${state}`
			});
		}
	},
	{
		path: "/about-us/terms-and-conditions/",
		component: TermsAndConditionsPage
	},
	{
		path: "/paid-landing/",
		component: PaidLandingPage
	},
	{
		path: "/self-storage-size-guide/",
		component: SizeGuidePage
	},
	{
		path: "/help/",
		component: HelpPage,
		actions: { getLandingPage },
		loadData: ({ actions, match, queryParams }) =>
			actions.getLandingPage(match, queryParams, "city")
	},
	{
		path: "/about-us/privacy/",
		component: PrivacyPolicyPage
	},
	{
		path: "/help/contact/",
		component: ContactUsPage
	},

	{
		path: "/self-storage/:state/:city/",
		component: SearchPage,
		actions: { getLandingPage },
		loadData: ({ actions, match, queryParams }) =>
			actions.getLandingPage(match, queryParams, "city")
	},
	// Dimension landing pages - one route per LANDING_PAGE_QUERY key, so the slug list
	// lives in one place. Must stay ABOVE the dynamic routes below (kept for correctness,
	// though static segments already win). redirectUnitTypeToClp.js also uses LANDING_PAGE_QUERY
	// as its slug allowlist, so the two can't drift apart.
	// params.amenity carries the raw slug - buildLandingQuery maps it to a gateway token.
	...Object.keys(LANDING_PAGE_QUERY).map((slug) => ({
		path: `/self-storage/:state/:city/${slug}/`,
		component: SearchPage,
		actions: { getLandingPage },
		loadData: ({ actions, match, queryParams }) =>
			actions.getLandingPage(
				{ ...match, params: { ...match.params, amenity: slug } },
				queryParams,
				"amenity"
			)
	})),
	{
		// https://storage.com/self-storage/texas/san-marcos/78666/1620-ih-35-s/
		path: "/self-storage/:state/:city/:zip/:address/",
		component: FacilityPage,
		actions: { getFacilityPage },
		loadData: ({ actions, match, queryParams }) => {
			const {
				params: { address, state, city }
			} = match;
			return actions.getFacilityPage({
				query: queryParams,
				path: `/${state}/${city}/${address}`
			});
		}
	},
	{
		path: "/self-storage/:state/:city/:facilitySlug/",
		component: FacilityPage,
		actions: { getFacilityPage },
		loadData: ({ actions, match, queryParams }) => {
			const { facilitySlug } = match.params;
			const facilityId = facilitySlug.split("-").pop();
			// facilitySlug is expected to end in "-<numeric facilityId>" (e.g.
			// "1620-ih-35-s-236289"). Amenity/category slugs that reach this
			// catch-all route (e.g. "...-self-storage-units") have no numeric
			// suffix; skip the fetch rather than send a bad id to the gateway.
			if (!/^\d+$/.test(facilityId)) return null;
			return actions.getFacilityPage({
				query: queryParams,
				path: `/${facilityId}`
			});
		}
	},
	{
		path: "/self-storage/:state/:city/:facilitySlug/reserve/:unitId/",
		component: UnitPageV2,
		actions: { getUnit },
		loadData: ({ actions, match, queryParams }) => {
			const { facilitySlug, unitId } = match.params;
			const facilityId = facilitySlug.split("-").pop();
			// See getFacilityPage's loadData above: skip the fetch when the slug
			// has no trailing numeric facilityId.
			if (!/^\d+$/.test(facilityId)) return null;
			return actions.getUnit({
				query: queryParams,
				path: `/${facilityId}/unit/${unitId}`
			});
		}
	},
	{
		path: "/ux/",
		component: StyleGuide
	},
	{
		path: "/style-guide/",
		component: StyleGuidePage
	},
	{
		component: ErrorPage
	}
];

export default function Routes({ environment }) {
	const filterPathDevelopment = ["/ux/", "/style-guide/"];
	const devEnvironments = ["development", "stage", "dev"];
	return (
		<RRRoutes>
			{(devEnvironments.includes(environment)
				? staticRoutes
				: staticRoutes.filter(
						(route) => !filterPathDevelopment.includes(route.path)
					)
			).map((route, i) => (
				<Route
					key={route.key || i}
					path={route.path ?? "*"}
					element={<route.component />}
				/>
			))}
		</RRRoutes>
	);
}

Routes.propTypes = {
	environment: PropTypes.string
};
